<template>
  <div class="labs-mix-detail">
    <div class="ui container">

      <div class="ui breadcrumb" style="margin: 20px 0 10px 0;">
        <router-link to="/" class="section">Home</router-link>
        <i class="right chevron icon divider"></i>
        <router-link to="/labs" class="section">Labs</router-link>
        <i class="right chevron icon divider"></i>
        <router-link to="/labs/mixes" class="section">Mixes</router-link>
        <i class="right chevron icon divider"></i>
        <div class="active section">{{ mix ? (mix.testNumber || 'Mix #' + mix.id) : 'Detail' }}</div>
      </div>

      <div v-if="loading" class="ui active centered inline loader"></div>

      <div v-else-if="error" class="ui negative message">
        <i class="close icon" @click="error = null"></i>
        <p>{{ error }}</p>
      </div>

      <div v-else-if="mix">

        <!-- Header -->
        <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:20px;">
          <h2 class="ui header" style="margin:0;">
            <i class="tint icon"></i>
            <div class="content">
              {{ mix.testNumber || 'Mix #' + mix.id }}
              <div class="sub header">{{ formatDate(mix.mixDate) }}</div>
            </div>
          </h2>
          <div>
            <button v-if="isAdmin && !editing" class="ui blue button" @click="startEdit">
              <i class="edit icon"></i> Edit
            </button>
            <router-link to="/labs/mixes" class="ui button">
              <i class="arrow left icon"></i> Back to Mixes
            </router-link>
          </div>
        </div>

        <!-- Success message -->
        <div v-if="saveSuccess" class="ui positive message">
          <i class="close icon" @click="saveSuccess = false"></i>
          <p>Mix saved successfully.</p>
        </div>

        <!-- ═══════════ EDIT MODE ═══════════ -->
        <div v-if="editing">
          <form class="ui form" @submit.prevent="saveMix">

            <h3 class="ui dividing header">Mix Info</h3>
            <div class="three fields">
              <div class="field">
                <label>Test Number</label>
                <input type="text" v-model="form.testNumber" placeholder="e.g. MIX-001" />
              </div>
              <div class="field">
                <label>Mix Date</label>
                <input type="date" v-model="form.mixDate" />
              </div>
              <div class="field">
                <label>Mixer Type</label>
                <select class="ui dropdown" v-model="form.mixerType">
                  <option value="">— Select —</option>
                  <option value="Eirich">Eirich</option>
                  <option value="Sigma">Sigma</option>
                  <option value="Planetary">Planetary</option>
                  <option value="Pan">Pan</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <h3 class="ui dividing header">Mix Conditions</h3>
            <div class="three fields">
              <div class="field">
                <label>Total Mix Weight Dry (g)</label>
                <input type="number" step="0.01" v-model.number="form.totalMixWeightDryG" />
              </div>
              <div class="field">
                <label>Water Added (g)</label>
                <input type="number" step="0.01" v-model.number="form.waterAddedG" />
              </div>
              <div class="field">
                <label>Mix LBD (g/cm³)</label>
                <input type="number" step="0.001" v-model.number="form.mixLbdGCm3" />
              </div>
            </div>
            <div class="three fields">
              <div class="field">
                <label>WBM Target %</label>
                <input type="number" step="0.01" v-model.number="form.mixWbmTargetPct" />
              </div>
              <div class="field">
                <label>WBM Measured %</label>
                <input type="number" step="0.01" v-model.number="form.mixWbmMeasuredPct" />
              </div>
              <div class="field">
                <label>Ambient Temp (°C)</label>
                <input type="number" step="0.1" v-model.number="form.ambientTempC" />
              </div>
            </div>
            <div class="two fields">
              <div class="field">
                <label>Mix Temp (°C)</label>
                <input type="number" step="0.1" v-model.number="form.mixTempC" />
              </div>
              <div class="field">
                <label>Mix Temp Note</label>
                <input type="text" v-model="form.mixTempNote" />
              </div>
            </div>
            <div class="field">
              <label>Technicians</label>
              <input type="text" v-model="form.techniciansText" placeholder="Names of technicians" />
            </div>
            <div class="field">
              <label>Comments</label>
              <textarea rows="3" v-model="form.comments"></textarea>
            </div>

            <h3 class="ui dividing header">Materials (A–G)</h3>
            <table class="ui celled table">
              <thead>
                <tr>
                  <th style="width:60px;">Slot</th>
                  <th>Material</th>
                  <th style="width:150px;">% Needed</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="slot in materialSlots" :key="slot">
                  <td><strong>{{ slot }}</strong></td>
                  <td>
                    <select class="ui dropdown" v-model.number="form['material' + slot + 'Id']">
                      <option :value="null">— None —</option>
                      <option v-for="mat in rawMaterials" :key="mat.id" :value="mat.id">
                        {{ mat.materialTrackingNumber || mat.materialDescription || 'Material #' + mat.id }}
                      </option>
                    </select>
                  </td>
                  <td>
                    <input type="number" step="0.01" v-model.number="form['material' + slot + 'PctNeeded']"
                           :disabled="!form['material' + slot + 'Id']" />
                  </td>
                </tr>
              </tbody>
            </table>

            <h3 class="ui dividing header">Visibility</h3>
            <div class="field">
              <div class="ui toggle checkbox">
                <input type="checkbox" v-model="form.visibleToCustomer" />
                <label>Visible to Customer</label>
              </div>
            </div>

            <div style="margin-top:20px; display:flex; gap:10px;">
              <button type="submit" class="ui green button" :class="{ loading: saving }">
                <i class="save icon"></i> Save Mix
              </button>
              <button type="button" class="ui button" @click="cancelEdit">Cancel</button>
            </div>

          </form>
        </div>

        <!-- ═══════════ VIEW MODE ═══════════ -->
        <div v-else>

          <!-- Mix Conditions -->
          <h3 class="ui dividing header">Mix Conditions</h3>
          <div class="ui three stackable cards" style="margin-bottom:20px;">
            <div class="card">
              <div class="content">
                <div class="header">WBM Target</div>
                <div class="description" style="font-size:1.4em; font-weight:bold;">
                  {{ mix.mixWbmTargetPct != null ? mix.mixWbmTargetPct + '%' : '—' }}
                </div>
              </div>
            </div>
            <div class="card">
              <div class="content">
                <div class="header">WBM Measured</div>
                <div class="description" style="font-size:1.4em; font-weight:bold;">
                  {{ mix.mixWbmMeasuredPct != null ? mix.mixWbmMeasuredPct + '%' : '—' }}
                </div>
              </div>
            </div>
            <div class="card">
              <div class="content">
                <div class="header">Total Dry Weight</div>
                <div class="description" style="font-size:1.4em; font-weight:bold;">
                  {{ mix.totalMixWeightDryG != null ? mix.totalMixWeightDryG.toLocaleString() + ' g' : '—' }}
                </div>
              </div>
            </div>
          </div>

          <table class="ui definition table" style="margin-bottom:30px;">
            <tbody>
              <tr><td>Water Added</td><td>{{ mix.waterAddedG != null ? mix.waterAddedG + ' g' : '—' }}</td></tr>
              <tr><td>Ambient Temp</td><td>{{ mix.ambientTempC != null ? mix.ambientTempC + ' °C' : '—' }}</td></tr>
              <tr><td>Mix Temp</td><td>{{ mix.mixTempC != null ? mix.mixTempC + ' °C' : '—' }}{{ mix.mixTempNote ? ' — ' + mix.mixTempNote : '' }}</td></tr>
              <tr><td>Mix LBD</td><td>{{ mix.mixLbdGCm3 != null ? mix.mixLbdGCm3 + ' g/cm³' : '—' }}</td></tr>
              <tr><td>Mixer Type</td><td>{{ mix.mixerType || '—' }}</td></tr>
              <tr><td>Technicians</td><td>{{ mix.techniciansText || '—' }}</td></tr>
              <tr><td>Comments</td><td>{{ mix.comments || '—' }}</td></tr>
            </tbody>
          </table>

          <!-- Materials -->
          <h3 class="ui dividing header">Materials</h3>
          <table class="ui celled table" style="margin-bottom:30px;">
            <thead>
              <tr>
                <th>Slot</th>
                <th>Material</th>
                <th>% Needed</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="slot in materialSlots">
                <tr v-if="mix['material' + slot + 'Id']" :key="slot">
                  <td><strong>{{ slot }}</strong></td>
                  <td>{{ getMaterialLabel(mix['material' + slot + 'Id']) }}</td>
                  <td>{{ mix['material' + slot + 'PctNeeded'] != null ? mix['material' + slot + 'PctNeeded'] + '%' : '—' }}</td>
                </tr>
              </template>
              <tr v-if="!hasAnyMaterial">
                <td colspan="3" class="center aligned">No materials assigned.</td>
              </tr>
            </tbody>
          </table>

          <!-- Extrusions from this mix -->
          <h3 class="ui dividing header">Extrusions from this Mix</h3>
          <div v-if="loadingExtrusions" class="ui active inline loader"></div>
          <div v-else-if="extrusions.length === 0" class="ui info message">
            <p>No extrusions recorded for this mix yet.</p>
          </div>
          <table v-else class="ui celled striped table">
            <thead>
              <tr>
                <th>Test Number</th>
                <th>Date</th>
                <th>Extruder Type</th>
                <th>WBM %</th>
                <th>Throughput</th>
                <th>Successful</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="ext in extrusions" :key="ext.id">
                <td>{{ ext.testNumber || '—' }}</td>
                <td>{{ formatDate(ext.extrusionDate) }}</td>
                <td>{{ ext.extruderType || '—' }}</td>
                <td>{{ ext.wbm != null ? ext.wbm + '%' : '—' }}</td>
                <td>{{ ext.throughputGMin != null ? ext.throughputGMin + ' g/min' : '—' }}</td>
                <td>
                  <span :class="ext.successful === 'Yes' ? 'ui green label' : ext.successful === 'No' ? 'ui red label' : 'ui grey label'">
                    {{ ext.successful || '—' }}
                  </span>
                </td>
                <td>
                  <router-link :to="'/labs/extrusions/' + ext.id" class="ui mini primary button">View</router-link>
                </td>
              </tr>
            </tbody>
          </table>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import httpClient from "@/assets/js/HttpClient";

export default {
  name: "LabsMixDetail",
  data() {
    return {
      mix: null,
      extrusions: [],
      rawMaterials: [],
      loading: true,
      loadingExtrusions: true,
      error: null,
      editing: false,
      saving: false,
      saveSuccess: false,
      form: {},
      materialSlots: ["A", "B", "C", "D", "E", "F", "G"],
    };
  },
  computed: {
    ...mapGetters({ currentUser: "currentUser" }),
    isAdmin() {
      return this.currentUser && this.currentUser.admin;
    },
    hasAnyMaterial() {
      if (!this.mix) return false;
      return this.materialSlots.some(
        (s) => this.mix["material" + s + "Id"] != null
      );
    },
  },
  methods: {
    formatDate(val) {
      if (!val) return "—";
      return new Date(val).toLocaleDateString();
    },

    getMaterialLabel(id) {
      if (!id) return "—";
      const mat = this.rawMaterials.find((m) => m.id === id);
      if (mat) {
        return (
          mat.materialTrackingNumber ||
          mat.materialDescription ||
          "Material #" + mat.id
        );
      }
      return "Material #" + id;
    },

    async loadMix() {
      try {
        const response = await httpClient.get(
          "/labs/mixes/" + this.$route.params.id
        );
        this.mix = response.data;
        // If this is a brand new mix (no test number), go straight to edit mode
        if (this.isAdmin && !this.mix.testNumber) {
          this.startEdit();
        }
      } catch (err) {
        this.error = "Unable to load mix details.";
      } finally {
        this.loading = false;
      }
    },

    async loadExtrusions() {
      try {
        const response = await httpClient.get(
          "/labs/extrusions/by-mix/" + this.$route.params.id
        );
        this.extrusions = response.data;
      } catch (err) {
        this.extrusions = [];
      } finally {
        this.loadingExtrusions = false;
      }
    },

    async loadRawMaterials() {
      try {
        const response = await httpClient.get("/labs/raw-materials");
        this.rawMaterials = response.data;
      } catch (err) {
        this.rawMaterials = [];
      }
    },

    startEdit() {
      // Clone mix data into form
      this.form = {
        testNumber: this.mix.testNumber || "",
        mixDate: this.mix.mixDate
          ? this.mix.mixDate.substring(0, 10)
          : "",
        mixerType: this.mix.mixerType || "",
        totalMixWeightDryG: this.mix.totalMixWeightDryG,
        waterAddedG: this.mix.waterAddedG,
        mixLbdGCm3: this.mix.mixLbdGCm3,
        mixWbmTargetPct: this.mix.mixWbmTargetPct,
        mixWbmMeasuredPct: this.mix.mixWbmMeasuredPct,
        ambientTempC: this.mix.ambientTempC,
        mixTempC: this.mix.mixTempC,
        mixTempNote: this.mix.mixTempNote || "",
        techniciansText: this.mix.techniciansText || "",
        comments: this.mix.comments || "",
        visibleToCustomer: this.mix.visibleToCustomer || false,
        // Material slots
        materialAId: this.mix.materialAId || null,
        materialAPctNeeded: this.mix.materialAPctNeeded,
        materialBId: this.mix.materialBId || null,
        materialBPctNeeded: this.mix.materialBPctNeeded,
        materialCId: this.mix.materialCId || null,
        materialCPctNeeded: this.mix.materialCPctNeeded,
        materialDId: this.mix.materialDId || null,
        materialDPctNeeded: this.mix.materialDPctNeeded,
        materialEId: this.mix.materialEId || null,
        materialEPctNeeded: this.mix.materialEPctNeeded,
        materialFId: this.mix.materialFId || null,
        materialFPctNeeded: this.mix.materialFPctNeeded,
        materialGId: this.mix.materialGId || null,
        materialGPctNeeded: this.mix.materialGPctNeeded,
      };
      this.editing = true;
      this.saveSuccess = false;
    },

    cancelEdit() {
      this.editing = false;
      this.form = {};
    },

    async saveMix() {
      this.saving = true;
      this.error = null;
      try {
        // Build the payload — send nulls for empty number fields
        const payload = {
          testNumber: this.form.testNumber || null,
          mixDate: this.form.mixDate || null,
          mixerType: this.form.mixerType || null,
          totalMixWeightDryG: this.form.totalMixWeightDryG || null,
          waterAddedG: this.form.waterAddedG || null,
          mixLbdGCm3: this.form.mixLbdGCm3 || null,
          mixWbmTargetPct: this.form.mixWbmTargetPct || null,
          mixWbmMeasuredPct: this.form.mixWbmMeasuredPct || null,
          ambientTempC: this.form.ambientTempC || null,
          mixTempC: this.form.mixTempC || null,
          mixTempNote: this.form.mixTempNote || null,
          techniciansText: this.form.techniciansText || null,
          comments: this.form.comments || null,
          visibleToCustomer: this.form.visibleToCustomer,
          materialAId: this.form.materialAId || null,
          materialAPctNeeded: this.form.materialAPctNeeded || null,
          materialBId: this.form.materialBId || null,
          materialBPctNeeded: this.form.materialBPctNeeded || null,
          materialCId: this.form.materialCId || null,
          materialCPctNeeded: this.form.materialCPctNeeded || null,
          materialDId: this.form.materialDId || null,
          materialDPctNeeded: this.form.materialDPctNeeded || null,
          materialEId: this.form.materialEId || null,
          materialEPctNeeded: this.form.materialEPctNeeded || null,
          materialFId: this.form.materialFId || null,
          materialFPctNeeded: this.form.materialFPctNeeded || null,
          materialGId: this.form.materialGId || null,
          materialGPctNeeded: this.form.materialGPctNeeded || null,
        };

        await httpClient.put("/labs/mixes/" + this.mix.id, payload);

        // Reload mix data to reflect saved state
        const response = await httpClient.get(
          "/labs/mixes/" + this.$route.params.id
        );
        this.mix = response.data;
        this.editing = false;
        this.saveSuccess = true;
      } catch (err) {
        this.error = "Unable to save mix. Please try again.";
      } finally {
        this.saving = false;
      }
    },
  },
  mounted() {
    this.loadMix();
    this.loadExtrusions();
    this.loadRawMaterials();
  },
};
</script>
